/**
 * Service for caching webpage images in session storage
 * Images are cached for 1 hour and automatically cleaned up
 */

const CACHE_PREFIX = 'imagePreview_';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

/**
 * Stores an image URL in the cache with timestamp
 * @param {string} url - The original webpage URL
 * @param {string} imageUrl - The extracted image URL
 */
export const cacheImage = (url, imageUrl) => {
  try {
    const cacheKey = CACHE_PREFIX + encodeURIComponent(url);
    const cacheData = {
      imageUrl,
      timestamp: Date.now()
    };
    sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.warn('Failed to cache image:', error);
  }
};

/**
 * Retrieves an image URL from the cache if it exists and hasn't expired
 * @param {string} url - The original webpage URL
 * @returns {string|null} - The cached image URL or null if not found/expired
 */
export const getCachedImage = (url) => {
  try {
    const cacheKey = CACHE_PREFIX + encodeURIComponent(url);
    const cacheData = sessionStorage.getItem(cacheKey);

    if (!cacheData) {
      return null;
    }

    const { imageUrl, timestamp } = JSON.parse(cacheData);
    const now = Date.now();

    // Check if cache has expired
    if (now - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return imageUrl;
  } catch (error) {
    console.warn('Failed to retrieve cached image:', error);
    return null;
  }
};

/**
 * Cleans up expired cache entries
 */
export const cleanupExpiredCache = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];

    // Iterate through all session storage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        try {
          const cacheData = JSON.parse(sessionStorage.getItem(key));
          if (now - cacheData.timestamp > CACHE_DURATION) {
            keysToRemove.push(key);
          }
        } catch (error) {
          // Invalid cache entry, mark for removal
          keysToRemove.push(key);
        }
      }
    }

    // Remove expired entries
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to cleanup expired cache:', error);
  }
};

/**
 * Initialize cache cleanup on service load
 */
cleanupExpiredCache();